import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterOutlet,
} from '@angular/router';
import { AuthenticationService } from '../../core/security/authentication.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, filter, map, Observable, of } from 'rxjs';
import { ExtractRolePipe } from '../../core/pipes/extract-role-pipe';
import { ConnectedUser } from '../../core/types/connected-user.interface';
import { DbtRunnerComponent } from '../../core/dbt-runner/dbt-runner-component';
import { Store } from '@ngrx/store';
import { setDropdownValue } from '../../state/dropdown.actions';
const MAPPING_TAB = 'mapping';
const PROGRAM_CONFIG_TAB = 'program-config';
const DATA_CALENDAR_TAB = 'data-calendar';
const BUDGET_TAB = 'budget';
const VARIABLE_COST_TAB = 'variable-cost';
const OPERATING_PARAMETERS_TAB = 'operating-parameter';

@Component({
  selector: 'rtpca-navbar',
  imports: [
    CommonModule,
    RouterOutlet,
    RouterLink,
    TranslateModule,
    ExtractRolePipe,
    DbtRunnerComponent,
  ],
  templateUrl: './navbar.component.html',
  styles: ['.dropdown-toggle { font-weight: 700 !important; }'],
})
export class NavbarComponent implements OnInit {
  private readonly translateService = inject(TranslateService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly router = inject(Router);
  private readonly store = inject(Store);

  public connectedUser: BehaviorSubject<ConnectedUser | null> = this.authenticationService.connectedUser;
  tabName$: Observable<string> = of('mapping');
  availableTabs = [
    { label: MAPPING_TAB, route: '/mapping' },
    { label: PROGRAM_CONFIG_TAB, route: '/program-config' },
    { label: BUDGET_TAB, route: '/budget' },
    { label: VARIABLE_COST_TAB, route: '/variable-cost' },
    { label: OPERATING_PARAMETERS_TAB, route: '/operating-parameter' },
    { label: DATA_CALENDAR_TAB, route: '/data-calendar' }
  ];
  selectedTab = MAPPING_TAB;
  activeTabIndex = 0;
  currentLang: string;
  refinery!: string;
  showNavbar = true;
  powerBiReports: { nameKey: string; url: string }[] = [];

  constructor() {
    const savedLanguage = localStorage.getItem('language') ?? 'en';
    this.translateService.use(savedLanguage);
    this.currentLang = savedLanguage;
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.showNavbar = event.url !== '/help';
      });
  }

  ngOnInit() {
    this.tabName$ = this.getTabName$();
    this.connectedUser.subscribe((user) => {
      if (user && !this.refinery) {
        const refineryRole = user.roles.find(role => role?.startsWith('ref'));
        if (refineryRole) {
          const [, refineryValue] = refineryRole.split('_');
          this.refinery = refineryValue;
          this.powerBiReports = this.getPowerBiReports(this.refinery);
        }
        const value = this.refinery;
        this.store.dispatch(setDropdownValue({ value }));
      }
    });
  }

  getTabName$(): Observable<string> {
    return this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event: NavigationEnd) => {
        this.activeTabIndex = this.availableTabs.findIndex(
          (tab) => tab.route === event.urlAfterRedirects
        );
        return this.availableTabs[this.activeTabIndex]?.label;
      })
    );
  }

  extractRole(roles: string[]): boolean {
    return roles.some((role) => role.includes('ref_'));
  }

  public logout() {
    this.authenticationService.logout();
  }

  public help() {
    this.router.navigate(['/help']);
  }

  onChangeTab(tab: string): void {
    this.selectedTab = tab;
  }
  switchLanguage(language: string) {
    this.translateService.use(language);
    localStorage.setItem('language', language);
  }

  onSelect(value: string) {
    this.refinery = value;
    this.powerBiReports = this.getPowerBiReports(value);
    this.store.dispatch(setDropdownValue({ value }));
  }

  getPowerBiReports(refinery: string): { nameKey: string; url: string }[] {
    const refineryReports: Record<string, { nameKey: string; url: string }[]> = {
      TRA: [
        {
          nameKey: 'powerBi.unitView',
          url: 'https://app.powerbi.com/groups/c37d2b75-cdc1-4c9b-82ec-e4491b346f0a/reports/761828b9-4233-47c6-b04f-546cde9bc15f?experience=power-bi',
        },
        {
          nameKey: 'powerBi.unitMargin',
          url: 'https://app.powerbi.com/groups/c37d2b75-cdc1-4c9b-82ec-e4491b346f0a/reports/80352d22-40c7-4d32-b465-93a0abac3309?experience=power-bi',
        },
        {
          nameKey: 'powerBi.checkAI',
          url: 'https://app.powerbi.com/groups/c37d2b75-cdc1-4c9b-82ec-e4491b346f0a/reports/68249a9d-eb7d-41ed-bd20-a99c50d1f6cc?experience=power-bi',
        },
      ],
      NOR: [
        {
          nameKey: 'powerBi.cpf',
          url: 'https://app.powerbi.com/groups/c37d2b75-cdc1-4c9b-82ec-e4491b346f0a/reports/6fa1daaa-6bde-44c7-928b-b3c604d62a5c/252625505acdcd6a104e?experience=power-bi',
        },
        {
          nameKey: 'powerBi.checkAI',
          url: 'https://app.powerbi.com/groups/c37d2b75-cdc1-4c9b-82ec-e4491b346f0a/reports/76317072-d771-40d1-897c-e1f3ff2f9674/ba9d58a785a4da330a6b?experience=power-bi',
        },
        {
          nameKey: 'powerBi.unitMargin',
          url: 'https://app.powerbi.com/groups/c37d2b75-cdc1-4c9b-82ec-e4491b346f0a/reports/17e3030c-8a78-4ff3-9a09-24d3d9c03f04?experience=power-bi',
        }
      ],
      UGO: [
        {
          nameKey: 'powerBi.cpf',
          url: 'https://app.powerbi.com/groups/c37d2b75-cdc1-4c9b-82ec-e4491b346f0a/reports/6fa1daaa-6bde-44c7-928b-b3c604d62a5c/252625505acdcd6a104e?experience=power-bi',
        },
        {
          nameKey: 'powerBi.checkAI',
          url: 'https://app.powerbi.com/groups/c37d2b75-cdc1-4c9b-82ec-e4491b346f0a/reports/76317072-d771-40d1-897c-e1f3ff2f9674/ba9d58a785a4da330a6b?experience=power-bi',
        },
        {
          nameKey: 'powerBi.unitMargin',
          url: 'https://app.powerbi.com/groups/c37d2b75-cdc1-4c9b-82ec-e4491b346f0a/reports/17e3030c-8a78-4ff3-9a09-24d3d9c03f04?experience=power-bi',
        }
      ],
      FZN: [
        {
          nameKey: 'powerBi.exploration',
          url: 'https://app.powerbi.com/groups/c37d2b75-cdc1-4c9b-82ec-e4491b346f0a/explorations/c577577e-7ece-4b16-8e15-20662fc3ef4f?experience=power-bi',
        },
        {
          nameKey: 'powerBi.unitMargin',
          url: 'https://app.powerbi.com/groups/c37d2b75-cdc1-4c9b-82ec-e4491b346f0a/reports/f2692298-b7f8-4c52-a21e-0d247b25db3d/25cbd70c19213c5bc5e1?experience=power-bi',
        },
        {
          nameKey: 'powerBi.unitView',
          url: 'https://app.powerbi.com/groups/c37d2b75-cdc1-4c9b-82ec-e4491b346f0a/reports/f2692298-b7f8-4c52-a21e-0d247b25db3d/25cbd70c19213c5bc5e1?experience=power-bi',
        },
        {
          nameKey: 'powerBi.checkAI',
          url: 'https://app.powerbi.com/groups/c37d2b75-cdc1-4c9b-82ec-e4491b346f0a/reports/a66b2928-d253-4d6a-a335-1b9b66497495/ba9d58a785a4da330a6b?experience=power-bi',
        },
      ],
      LEU: [
        {
          nameKey: 'powerBi.unitMargin',
          url: 'https://app.powerbi.com/groups/c37d2b75-cdc1-4c9b-82ec-e4491b346f0a/reports/6d9e3af5-fc99-4d17-8f52-57994d0ee4c5?experience=power-bi',
        },
        {
          nameKey: 'powerBi.checkAI',
          url: 'https://app.powerbi.com/groups/c37d2b75-cdc1-4c9b-82ec-e4491b346f0a/reports/8a447098-8675-4a15-85e9-ac3ad65bc5e1/ba9d58a785a4da330a6b?experience=power-bi',
        },
        {
          nameKey: 'powerBi.unitView',
          url: 'https://app.powerbi.com/groups/c37d2b75-cdc1-4c9b-82ec-e4491b346f0a/reports/f2338dda-901f-434c-8a6f-c16485a4c954/dc3ea07be13e33dcc045?experience=power-bi',
        },
      ]
    };

    return refineryReports[refinery] ?? [];
  }
}
